import { Link } from "react-router-dom";
import { Icon } from "@/components/layout/Icon";
import { EmptyState } from "@/components/ui/EmptyState";
import { timeAgo } from "@/lib/utils/format";
import type { AppNotification } from "@/types/domain";

// Maps a notification's module prefix ("leave.approved" -> "leave") to an
// icon/tone - purely presentational grouping of the real notification feed,
// not a second event system.
const TYPE_META: Record<string, { icon: string; tone: string }> = {
  leave: { icon: "calendar-check", tone: "bg-emerald-50 text-emerald-600" },
  attendance: { icon: "clock", tone: "bg-orange-50 text-orange-600" },
  payroll: { icon: "wallet", tone: "bg-amber-50 text-amber-600" },
  payslip: { icon: "file-text", tone: "bg-amber-50 text-amber-600" },
  employee: { icon: "users", tone: "bg-primary-50 text-primary-600" },
  expense: { icon: "wallet", tone: "bg-indigo-50 text-indigo-600" },
  document: { icon: "file-text", tone: "bg-slate-100 text-slate-600" },
  performance: { icon: "target", tone: "bg-indigo-50 text-indigo-600" },
  recruitment: { icon: "briefcase", tone: "bg-orange-50 text-orange-600" },
  salary_advance: { icon: "credit-card", tone: "bg-amber-50 text-amber-600" },
  bonus: { icon: "gift", tone: "bg-emerald-50 text-emerald-600" },
};

function iconFor(type: string) {
  const [module] = type.split(".");
  return TYPE_META[module ?? ""] ?? { icon: "bell", tone: "bg-slate-100 text-slate-600" };
}

export function RecentActivities({ items }: { items: AppNotification[] }) {
  if (items.length === 0) {
    return <EmptyState icon={<Icon name="bell" className="h-7 w-7" />} title="No recent activity" description="Updates across your workspace will show up here." />;
  }

  return (
    <ul className="divide-y divide-slate-100">
      {items.map((item) => {
        const meta = iconFor(item.type);
        const row = (
          <div className="flex items-start gap-3 py-3">
            <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${meta.tone}`}>
              <Icon name={meta.icon} className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-slate-800">{item.title}</p>
              {item.message && <p className="truncate text-xs text-slate-500">{item.message}</p>}
            </div>
            <span className="shrink-0 whitespace-nowrap text-xs text-slate-400">{timeAgo(item.created_at)}</span>
          </div>
        );
        return (
          <li key={item.id}>
            {item.link ? (
              <Link to={item.link} className="-mx-1 block rounded-lg px-1 transition-colors duration-200 ease-out hover:bg-primary-50/40">
                {row}
              </Link>
            ) : (
              row
            )}
          </li>
        );
      })}
    </ul>
  );
}
